import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PageHero from '../components/PageHero';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

export const ResourcesPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <PageHero 
        badge="Learn More"
        title="Car Care"
        titleAccent="Resources"
        subtitle="Expert advice and tips to help you keep your vehicle looking its best."
      />

      {/* Posts */}
      <section className="bg-white py-20 md:py-32" data-testid="resources-posts">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Search */}
          <motion.div {...fadeInUp} className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-3 border-slate-200 focus:border-[#1E3A8A] focus:ring-[#1E3A8A] rounded-full"
                data-testid="resources-search-input"
              />
            </div>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#1E3A8A] border-t-transparent rounded-full spinner mx-auto mb-4" />
              <p className="text-slate-500">Loading resources...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">No resources found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, idx) => (
                <motion.article
                  key={post.id}
                  {...fadeInUp}
                  transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  data-testid={`resource-post-${post.slug}`}
                >
                  {post.featured_image && (
                    <Link to={`/resources/${post.slug}`}>
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={post.featured_image} 
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </div>
                    </Link>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                    <Link to={`/resources/${post.slug}`}>
                      <h2 className="text-xl font-bold text-[#0F172A] mb-3 group-hover:text-[#1E3A8A] transition-colors">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link 
                      to={`/resources/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#1E3A8A] font-medium hover:text-[#DC2626] transition-colors"
                      data-testid={`resource-read-more-${post.slug}`}
                    >
                      Read More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 py-20 md:py-24" data-testid="resources-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] mb-6">
              Questions About Car Care?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Our team is always happy to help with car care advice. Stop by or give us a call!
            </p>
            <Link to="/contact" data-testid="resources-contact-btn">
              <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white font-bold text-lg px-10 py-4 rounded-full">
                Contact Us
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
